import React, { useState, useEffect, useCallback } from 'react';
import './FriendsMenu.css';
import { 
    X, Search, UserPlus, Users, 
    Trophy, Coins, Trash2, Check, User,
    Swords, Minus, Plus, Share2, Clock,
    LogIn, Save, LayoutGrid, Play
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useGame } from '../utils/GameContext'; 
import { useAudio } from '../utils/AudioContext'; // Import Audio
import { 
    playUiHoverSound, 
    playUiClickSound, 
    playUiConfirmSound, 
    playUiOpenSound, 
    playUiCloseSound 
} from '../utils/sounds'; // Import Sounds
import { AVATARS } from './AccountMenu';

function FriendsMenu({ onClose, onOpenLogin }) {
    const { 
        currentUser, 
        userData, 
        searchUserByUsername, 
        checkFriendStatus,
        sendFriendRequest, 
        removeFriendRequest,
        getPublicProfile,
        acceptFriendRequest,
        removeFriend,
        removeChallenge 
    } = useAuth();

    const { setGameMode, setChallengeData, setGameState } = useGame();
    const { sfxMuted } = useAudio();

    const [activeTab, setActiveTab] = useState('list'); 
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResult, setSearchResult] = useState(null);
    const [searchStatus, setSearchStatus] = useState('idle'); 
    const [friendShipStatus, setFriendShipStatus] = useState('none');
    
    const [friendsList, setFriendsList] = useState([]);
    const [requestsList, setRequestsList] = useState([]);
    const [challengesList, setChallengesList] = useState([]); 
    const [loadingData, setLoadingData] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    // Animation States
    const [isExiting, setIsExiting] = useState(false);
    const [exitType, setExitType] = useState(null); 
    const [isSwitchingToChallenge, setIsSwitchingToChallenge] = useState(false); 
    const [isChallengeExiting, setIsChallengeExiting] = useState(false); 

    // Modals
    const [showChallengeModal, setShowChallengeModal] = useState(false);
    const [challengeTarget, setChallengeTarget] = useState(null);
    const [betAmount, setBetAmount] = useState(100);

    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [isRemoveModalExiting, setIsRemoveModalExiting] = useState(false);

    // Insufficient Funds States
    const [showFundsModal, setShowFundsModal] = useState(false);
    const [isFundsModalExiting, setIsFundsModalExiting] = useState(false); 
    const [shakeBtnId, setShakeBtnId] = useState(null);

    // Play Open Sound on Mount
    useEffect(() => {
        playUiOpenSound(sfxMuted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ... Helpers & Data Loading (Same as before) ...
    const getAvatarIcon = (id) => {
        const avatar = AVATARS.find(a => a.id === id);
        return avatar ? avatar.icon : <User />;
    };

    const getAvatarColor = (id) => {
        const avatar = AVATARS.find(a => a.id === id);
        return avatar ? avatar.color : '#ffffff';
    };

    useEffect(() => {
        const loadFriendsData = async () => {
            if (!userData) return;
            setLoadingData(true);

            if (userData.friends) {
                const friendUids = Object.keys(userData.friends);
                const profiles = await Promise.all(friendUids.map(uid => getPublicProfile(uid)));
                setFriendsList(profiles.filter(p => p !== null));
            } else {
                setFriendsList([]);
            }

            if (userData.friendRequests) {
                const reqUids = Object.keys(userData.friendRequests);
                const profiles = await Promise.all(reqUids.map(uid => getPublicProfile(uid)));
                setRequestsList(profiles.filter(p => p !== null));
            } else {
                setRequestsList([]);
            }

            if (userData.challenges) {
                const cList = Object.entries(userData.challenges).map(([id, data]) => ({
                    id,
                    ...data
                }));
                setChallengesList(cList);
            } else {
                setChallengesList([]);
            }

            setLoadingData(false);
        };

        if (currentUser) {
            loadFriendsData();
        }
    }, [userData, getPublicProfile, currentUser]);

    // --- Handlers ---
    
    const handleClose = useCallback(() => {
        playUiCloseSound(sfxMuted);
        setExitType('close');
        setIsExiting(true);
    }, [sfxMuted]);

    const handleLoginClick = () => {
        playUiConfirmSound(sfxMuted);
        setExitType('login');
        setIsExiting(true);
    };

    const handleFriendsMenuAnimationEnd = (e) => {
        if (e.target !== e.currentTarget) return;
        
        if (isSwitchingToChallenge) {
            setShowChallengeModal(true);
            setIsSwitchingToChallenge(false);
            return;
        }

        if (isExiting) {
            if (exitType === 'login') onOpenLogin();
            else onClose();
        }
    };

    const handleChallengeModalAnimationEnd = (e) => {
        if (e.target !== e.currentTarget) return;

        if (isChallengeExiting) {
            setShowChallengeModal(false);
            setIsChallengeExiting(false);
            return;
        }

        if (isExiting) {
            onClose();
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        playUiClickSound(sfxMuted);
        if (!searchTerm.trim()) return;
        setSearchStatus('loading');
        setSearchResult(null);
        setFriendShipStatus('none');

        const uid = await searchUserByUsername(searchTerm.trim());
        if (uid) {
            if (uid === currentUser.uid) {
                setSearchStatus('self');
            } else {
                const profile = await getPublicProfile(uid);
                setSearchResult(profile);
                const status = await checkFriendStatus(uid);
                setFriendShipStatus(status);
                setSearchStatus('found');
            }
        } else {
            setSearchStatus('not-found');
        }
    };

    const handleSendRequest = async () => {
        if (!searchResult) return;
        playUiConfirmSound(sfxMuted);
        const res = await sendFriendRequest(searchResult.uid);
        if (res === 'success') {
            setFriendShipStatus('pending');
        }
    };

    const handleRevokeRequest = async () => {
        if (!searchResult) return;
        playUiClickSound(sfxMuted);
        await removeFriendRequest(searchResult.uid, false);
        setFriendShipStatus('none');
    };

    const handleShareLink = async () => {
        playUiConfirmSound(sfxMuted);
        const link = `${window.location.origin}?invite=${currentUser.uid}`;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Tile Sprint Invite',
                    text: `Add ${userData?.username || 'me'} as a friend on Tile Sprint!`,
                    url: link,
                });
            } catch (err) {
                console.log('Share canceled or failed', err);
            }
        } else {
            navigator.clipboard.writeText(link).then(() => {
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
            });
        }
    };

    const openChallenge = (friend) => {
        playUiConfirmSound(sfxMuted);
        setChallengeTarget(friend);
        setBetAmount(100); 
        setIsSwitchingToChallenge(true); 
    };

    const closeChallenge = () => {
        playUiClickSound(sfxMuted);
        setIsChallengeExiting(true); 
    };

    const handleBetChange = (amount) => {
        playUiClickSound(sfxMuted);
        const max = userData?.money || 0;
        if (amount < 0) amount = 0;
        if (amount > max) amount = max;
        setBetAmount(amount);
    };

    const startChallengeRun = () => {
        if (!challengeTarget) return;
        playUiConfirmSound(sfxMuted);
        setGameMode('create_challenge');
        setChallengeData({
            targetUid: challengeTarget.uid,
            wager: betAmount
        });
        setGameState('ready');
        handleClose(); 
    };

    const handleAcceptClick = (challenge) => {
        playUiConfirmSound(sfxMuted);
        const userMoney = userData?.money || 0;
        
        if (userMoney < challenge.wager) {
            setShakeBtnId(challenge.id);
            setTimeout(() => setShakeBtnId(null), 500); 
            setShowFundsModal(true);
            return;
        }

        setGameMode('attempt_challenge');
        setChallengeData({
            challengeId: challenge.id,
            senderUid: challenge.senderUid, 
            wager: challenge.wager,
            scoreToBeat: challenge.scoreToBeat,
            senderName: challenge.senderName
        });
        setGameState('ready');
        handleClose(); 
    };

    const openRemoveConfirm = (friend) => {
        playUiClickSound(sfxMuted);
        setRemoveTarget(friend);
        setShowRemoveModal(true);
    };

    const confirmRemove = async () => {
        if (removeTarget) {
            playUiConfirmSound(sfxMuted);
            await removeFriend(removeTarget.uid);
            setIsRemoveModalExiting(true); // Trigger exit animation
        }
    };

    const handleRemoveCancel = () => {
        playUiClickSound(sfxMuted);
        setIsRemoveModalExiting(true); // Trigger exit animation
    };

    const onRemoveModalAnimationEnd = (e) => {
        if (e.target !== e.currentTarget) return; // Prevent bubbling events
        if (isRemoveModalExiting) {
            setShowRemoveModal(false);
            setRemoveTarget(null);
            setIsRemoveModalExiting(false);
        }
    };

    const handleFundsModalClose = () => {
        playUiClickSound(sfxMuted);
        setIsFundsModalExiting(true);
    };

    const onFundsModalAnimationEnd = () => {
        if (isFundsModalExiting) {
            setShowFundsModal(false);
            setIsFundsModalExiting(false);
        }
    };

    const renderAddButton = () => {
        if (friendShipStatus === 'friends') {
            return <button className="add-btn-large added" disabled><Check size={18} /> ADDED</button>;
        } else if (friendShipStatus === 'pending') {
            return <button className="add-btn-large pending" onClick={handleRevokeRequest} onMouseEnter={() => playUiHoverSound(sfxMuted)}><Clock size={18} /> PENDING</button>;
        } else {
            return <button className="add-btn-large" onClick={handleSendRequest} onMouseEnter={() => playUiHoverSound(sfxMuted)}><UserPlus size={18} /> ADD FRIEND</button>;
        }
    };

    // --- AUTH LOGIN WALL ---
    if (!currentUser) {
        return (
            <div 
                className={`friends-menu-overlay ${isExiting ? 'fade-out' : 'fade-in'}`}
                style={{ pointerEvents: isExiting ? 'none' : 'auto' }}
            >
                <div 
                    className={`friends-menu-container auth-prompt-modal enhanced ${isExiting ? 'pop-out' : ''}`}
                    onAnimationEnd={handleFriendsMenuAnimationEnd} 
                >
                    {/* ... (Keep existing login UI) ... */}
                    <div className="auth-header">
                        <div className="icon-glow-container">
                            <Users size={48} className="c-orange" />
                        </div>
                        <h2>Social Hub</h2>
                        <p className="subtitle">Connect, compete, and wager coins against friends!</p>
                    </div>

                    <div className="feature-grid">
                         {/* ... Features ... */}
                        <div className="feature-item">
                            <div className="f-icon bg-orange"><Swords size={20} /></div>
                            <div className="f-text">
                                <span>Challenges</span>
                                <small>Wager coins & win big</small>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="f-icon bg-green"><Save size={20} /></div>
                            <div className="f-text">
                                <span>Cloud Sync</span>
                                <small>Keep your friends list safe</small>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="f-icon bg-purple"><LayoutGrid size={20} /></div>
                            <div className="f-text">
                                <span>Custom Avatar</span>
                                <small>Show off your style</small>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="f-icon bg-blue"><UserPlus size={20} /></div>
                            <div className="f-text">
                                <span>Connect</span>
                                <small>Find and add players</small>
                            </div>
                        </div>
                    </div>

                    <div className="auth-actions">
                        <button onClick={handleLoginClick} className="btn-login-pulse" onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                            <LogIn size={20} />
                            <span>Sign In / Register</span>
                        </button>
                        <button onClick={handleClose} className="btn-text-only" onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                            Maybe Later
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const totalRequests = requestsList.length + challengesList.length;

    // --- MAIN MENU RENDER ---
    return (
        <div 
            className={`friends-menu-overlay ${isExiting ? 'fade-out' : 'fade-in'}`}
            style={{ pointerEvents: isExiting ? 'none' : 'auto' }}
        >
            {!showChallengeModal && (
                <div 
                    className={`friends-menu-container ${isExiting || isSwitchingToChallenge ? 'pop-out' : ''}`}
                    onAnimationEnd={handleFriendsMenuAnimationEnd}
                >
                    <div className="friends-header">
                        <div className="header-title-group">
                            <Users className="c-orange" size={32} />
                            <h1>FRIENDS</h1>
                        </div>
                        <button className="close-btn" onClick={handleClose} onMouseEnter={() => playUiHoverSound(sfxMuted)}><X size={24}/></button>
                    </div>

                    <div className="friends-tabs">
                        <button 
                            className={activeTab === 'list' ? 'active' : ''} 
                            onClick={() => { playUiClickSound(sfxMuted); setActiveTab('list'); }}
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        >
                            My List
                        </button>
                        <button 
                            className={activeTab === 'add' ? 'active' : ''} 
                            onClick={() => { playUiClickSound(sfxMuted); setActiveTab('add'); }}
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        >
                            Add Friend
                        </button>
                        <button 
                            className={activeTab === 'requests' ? 'active' : ''} 
                            onClick={() => { playUiClickSound(sfxMuted); setActiveTab('requests'); }}
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        >
                            Requests
                            {totalRequests > 0 && <span className="tab-badge">{totalRequests}</span>}
                        </button>
                    </div>

                    <div className="friends-content-area custom-scroll">
                        {/* List Tab */}
                        {activeTab === 'list' && (
                            <div className="tab-pane fade-in">
                                {loadingData ? (
                                    <div className="loading-spinner"><div className="spinner"></div></div>
                                ) : friendsList.length === 0 ? (
                                    <div className="empty-state">
                                        <Users size={48} className="empty-icon"/>
                                        <p>No friends yet.</p>
                                        <button className="action-btn" onClick={() => setActiveTab('add')} onMouseEnter={() => playUiHoverSound(sfxMuted)}>Find Players</button>
                                    </div>
                                ) : (
                                    <div className="friends-list-grid">
                                        {friendsList.map(friend => (
                                            <div key={friend.uid} className="friend-card-main">
                                                <div 
                                                    className="fc-avatar"
                                                    style={{ 
                                                        borderColor: getAvatarColor(friend.avatarId),
                                                        color: getAvatarColor(friend.avatarId)
                                                    }}
                                                >
                                                    {getAvatarIcon(friend.avatarId)}
                                                </div>
                                                <div className="fc-data-column">
                                                    <div className="fc-inner-card name-card">
                                                        <span className="fc-name">{friend.username}</span>
                                                    </div>
                                                    <div className="fc-inner-card score-card">
                                                        <Trophy size={16} className="c-yellow" />
                                                        <span className="fc-score">{friend.highscore}</span>
                                                    </div>
                                                </div>
                                                <div className="fc-action-column">
                                                    <button className="fc-challenge-btn" onClick={() => openChallenge(friend)} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                                        <Swords size={20} />
                                                        <span>CHALLENGE</span>
                                                    </button>
                                                    <button className="fc-remove-btn" onClick={() => openRemoveConfirm(friend)} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                                        <Trash2 size={20} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Add Tab */}
                        {activeTab === 'add' && (
                            <div className="tab-pane fade-in">
                                <div className="add-friend-section">
                                    <div className="invite-card">
                                        <div className="invite-content">
                                            <div className="icon-box"><Share2 size={24}/></div>
                                            <div className="invite-text">
                                                <h4>Share Invite</h4>
                                                <p>Share a link to add friends instantly.</p>
                                            </div>
                                        </div>
                                        <button 
                                            className={`copy-btn-large ${linkCopied ? 'copied' : ''}`} 
                                            onClick={handleShareLink}
                                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                        >
                                            {linkCopied ? 'LINK COPIED!' : 'SHARE LINK'}
                                        </button>
                                    </div>
                                    <div className="search-section-large">
                                        <h3>Search Users</h3>
                                        <form onSubmit={handleSearch} className="search-bar-large">
                                            <Search className="search-icon" size={20}/>
                                            <input 
                                                type="text" 
                                                placeholder="Enter username..." 
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </form>
                                        <div className="search-results-area">
                                            {searchStatus === 'loading' && <div className="loading-spinner small"></div>}
                                            {searchStatus === 'not-found' && <p className="status-msg error">User not found.</p>}
                                            {searchStatus === 'self' && <p className="status-msg error">You cannot add yourself.</p>}
                                            {searchStatus === 'found' && searchResult && (
                                                <div className="user-found-card-large">
                                                    <div 
                                                        className="found-avatar-large"
                                                        style={{ 
                                                            borderColor: getAvatarColor(searchResult.avatarId),
                                                            color: getAvatarColor(searchResult.avatarId)
                                                        }}
                                                    >
                                                        {getAvatarIcon(searchResult.avatarId)}
                                                    </div>
                                                    <div className="found-info-large">
                                                        <span className="found-name">{searchResult.username}</span>
                                                        <span className="found-score">High Score: {searchResult.highscore}</span>
                                                    </div>
                                                    {renderAddButton()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Requests Tab */}
                        {activeTab === 'requests' && (
                            <div className="tab-pane fade-in">
                                {totalRequests === 0 ? (
                                    <div className="empty-state">
                                        <p>No pending requests.</p>
                                    </div>
                                ) : (
                                    <div className="requests-list">
                                        {/* 1. Challenges First */}
                                        {challengesList.map(chal => (
                                            <div key={chal.id} className="request-card-large challenge-invite">
                                                <div 
                                                    className="req-avatar-large"
                                                    style={{ 
                                                        borderColor: getAvatarColor(chal.senderAvatarId),
                                                        color: getAvatarColor(chal.senderAvatarId)
                                                    }}
                                                >
                                                    {getAvatarIcon(chal.senderAvatarId)}
                                                </div>
                                                <div className="request-info-large">
                                                    <span className="req-label">CHALLENGE RECEIVED</span>
                                                    <span className="req-name">From: {chal.senderName}</span>
                                                    <div className="req-challenge-details">
                                                        <span className="detail">
                                                            <Coins size={14} className="c-yellow"/> {chal.wager}
                                                        </span>
                                                        <span className="detail">
                                                            <Trophy size={14} className="c-orange"/> Beat: {chal.scoreToBeat}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="request-actions-large">
                                                    <button 
                                                        className={`req-btn accept ${shakeBtnId === chal.id ? 'shake' : ''}`} 
                                                        onClick={() => handleAcceptClick(chal)}
                                                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                                        style={{width: 'auto', padding: '0 12px'}}
                                                    >
                                                        <Play size={18} fill="black" />
                                                    </button>
                                                    <button className="req-btn decline" onClick={() => removeChallenge(chal.id)} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                                        <X size={20}/>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}

                                        {/* 2. Friend Requests */}
                                        {requestsList.map(req => (
                                            <div key={req.uid} className="request-card-large">
                                                <div 
                                                    className="req-avatar-large"
                                                    style={{ 
                                                        borderColor: getAvatarColor(req.avatarId),
                                                        color: getAvatarColor(req.avatarId)
                                                    }}
                                                >
                                                    {getAvatarIcon(req.avatarId)}
                                                </div>
                                                <div className="request-info-large">
                                                    <span className="req-name">{req.username}</span>
                                                    <span className="req-label">Incoming Request</span>
                                                </div>
                                                <div className="request-actions-large">
                                                    <button className="req-btn accept" onClick={() => acceptFriendRequest(req.uid)} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                                        <Check size={20}/>
                                                    </button>
                                                    <button className="req-btn decline" onClick={() => removeFriendRequest(req.uid)} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                                        <X size={20}/>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* --- CHALLENGE MODAL --- */}
            {showChallengeModal && challengeTarget && (
                <div 
                    className={`challenge-modal-container ${isChallengeExiting || isExiting ? 'pop-out' : ''}`}
                    onAnimationEnd={handleChallengeModalAnimationEnd}
                >
                    <div className="challenge-header">
                        <h2>CHALLENGE REQUEST</h2>
                    </div>

                    <div className="challenge-vs-area">
                        {/* ... VS Area ... */}
                        <div className="vs-player left">
                            <div className="vs-avatar" style={{ borderColor: getAvatarColor(userData?.avatarId || 0), color: getAvatarColor(userData?.avatarId || 0) }}>
                                {getAvatarIcon(userData?.avatarId || 0)}
                            </div>
                            <span className="vs-name">YOU</span>
                        </div>
                        <div className="vs-divider">
                            <div className="swords-circle"><Swords size={36} className="c-orange" /></div>
                            <span className="vs-text">VS</span>
                        </div>
                        <div className="vs-player right">
                            <div className="vs-avatar" style={{ borderColor: getAvatarColor(challengeTarget.avatarId), color: getAvatarColor(challengeTarget.avatarId) }}>
                                {getAvatarIcon(challengeTarget.avatarId)}
                            </div>
                            <span className="vs-name">{challengeTarget.username}</span>
                        </div>
                    </div>

                    <div className="challenge-settings">
                        <div className="setting-label">WAGER AMOUNT</div>
                        
                        <div className="wager-control-row">
                            <button className="wager-adjust-btn" onClick={() => handleBetChange(betAmount - 100)} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                <Minus size={20} />
                            </button>
                            
                            <div className="wager-display">
                                <Coins size={24} className="c-yellow" />
                                <input 
                                    type="number" 
                                    min="0"
                                    value={betAmount} 
                                    onChange={(e) => handleBetChange(Number(e.target.value))}
                                />
                            </div>

                            <button className="wager-adjust-btn" onClick={() => handleBetChange(betAmount + 100)} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                <Plus size={20} />
                            </button>
                        </div>

                        <div className="quick-wager-row">
                            <button onClick={() => handleBetChange(100)} onMouseEnter={() => playUiHoverSound(sfxMuted)}>100</button>
                            <button onClick={() => handleBetChange(500)} onMouseEnter={() => playUiHoverSound(sfxMuted)}>500</button>
                            <button onClick={() => handleBetChange(1000)} onMouseEnter={() => playUiHoverSound(sfxMuted)}>1K</button>
                            <button onClick={() => handleBetChange(userData?.money || 0)} className="max-btn" onMouseEnter={() => playUiHoverSound(sfxMuted)}>MAX</button>
                        </div>

                        <div className="wallet-balance">
                            <span>Available Balance:</span>
                            <span className="balance-value">
                                <Coins size={14} className="c-yellow"/> {userData?.money || 0}
                            </span>
                        </div>
                    </div>

                    <div className="challenge-actions">
                        <button className="cancel-btn-large" onClick={closeChallenge} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                            CANCEL
                        </button>
                        <button 
                            className="confirm-btn-large" 
                            onClick={startChallengeRun}
                            disabled={(userData?.money || 0) < betAmount || betAmount <= 0}
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        >
                            <Play size={20} fill="black" />
                            START RUN
                        </button>
                    </div>
                </div>
            )}

            {/* --- INSUFFICIENT FUNDS MODAL --- */}
            {showFundsModal && (
                <div className={`mini-modal-overlay ${isFundsModalExiting ? 'fade-out' : ''}`}>
                     <div 
                        className={`mini-modal-content ${isFundsModalExiting ? 'pop-out' : ''}`}
                        onAnimationEnd={onFundsModalAnimationEnd}
                     >
                        <div className="warning-icon-circle">
                            <Coins size={32} />
                        </div>
                        <h3>Insufficient Funds</h3>
                        <p>
                            You don't have enough coins to accept this challenge.
                            <br/>Play more games to earn coins!
                        </p>
                        <div className="mini-modal-actions">
                            <button className="modal-btn cancel" onClick={handleFundsModalClose} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- CONFIRM REMOVE MODAL --- */}
            {showRemoveModal && removeTarget && (
                <div className={`mini-modal-overlay ${isRemoveModalExiting ? 'fade-out' : ''}`}>
                     <div 
                        className={`mini-modal-content ${isRemoveModalExiting ? 'pop-out' : ''}`}
                        onAnimationEnd={onRemoveModalAnimationEnd}
                     >
                        <div className="danger-icon-circle">
                            <Trash2 size={32} />
                        </div>
                        <h3>Remove Friend?</h3>
                        <p>
                            Are you sure you want to remove <span className="highlight-name">{removeTarget.username}</span>?
                            <br/>This action cannot be undone.
                        </p>
                        <div className="mini-modal-actions">
                            <button className="modal-btn cancel" onClick={handleRemoveCancel} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                CANCEL
                            </button>
                            <button className="modal-btn danger" onClick={confirmRemove} onMouseEnter={() => playUiHoverSound(sfxMuted)}>
                                REMOVE
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FriendsMenu;